<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :draggable="false"
    :closable="true"
    :style="{ width: showFunnelOption ? '55vw' : '500px', maxWidth: showFunnelOption ? '900px' : '500px' }"
    @hide="handleCancel"
  >
    <template #header>
      <div class="wysiwyg-link-dialog__header">
        <i class="fa-light fa-link" />
        <span>{{ t('templateBuilder.wysiwygLinkDialog.header') }}</span>
      </div>
    </template>

    <!-- Link text (applies to all modes) -->
    <div class="wysiwyg-link-dialog__text-section">
      <label>{{ t('templateBuilder.wysiwygLinkDialog.textLabel') }}</label>
      <InputText
        v-model="textValue"
        :placeholder="t('templateBuilder.wysiwygLinkDialog.textPlaceholder')"
        class="w-full"
      />
    </div>

    <!-- Link type selector (only if funnel is available and not funnel-only mode) -->
    <div
      v-if="showFunnelOption && !funnelOnly"
      class="wysiwyg-link-dialog__type-selector"
    >
      <label>{{ t('templateBuilder.wysiwygLinkDialog.linkType') }}</label>
      <Dropdown
        v-model="linkMode"
        :options="linkModeOptions"
        option-label="label"
        option-value="value"
        class="w-full"
        @change="handleLinkModeChange"
      />
    </div>

    <!-- URL mode (hidden in funnel-only mode) -->
    <div
      v-if="linkMode === 'url' && !funnelOnly"
      class="wysiwyg-link-dialog__url-section"
    >
      <label>{{ t('templateBuilder.wysiwygLinkDialog.urlLabel') }}</label>
      <div class="wysiwyg-link-dialog__url-row">
        <InputText
          ref="urlInputRef"
          v-model="urlValue"
          :placeholder="t('templateBuilder.wysiwygLinkDialog.urlPlaceholder')"
          class="flex-1"
          :class="{ 'p-invalid': urlError }"
          @click="saveCursorPosition"
          @keyup="saveCursorPosition"
          @keydown.enter="handleConfirm"
        />
        <VariablesButton
          v-if="variablesList.length > 0"
          :variables-list="variablesList"
          class="wysiwyg-link-dialog__variables-btn"
          @on-choose-variable="insertVariable"
        />
      </div>
      <small
        v-if="urlError"
        class="p-error"
      >
        {{ urlError }}
      </small>
      <small
        v-if="urlWarning && !urlError"
        class="wysiwyg-link-dialog__warning"
      >
        <i class="fa-light fa-triangle-exclamation" />
        {{ urlWarning }}
      </small>
    </div>

    <!-- Funnel mode: selected step info card -->
    <div
      v-if="linkMode === 'funnel' && hasSelectedRedirect && !showNextStepGrid"
      class="wysiwyg-link-dialog__funnel-info"
    >
      <div class="next-step-info">
        <div class="next-step-info__card">
          <div class="next-step-info__icon">
            <i
              :class="isGameRedirect
                ? 'fa-light fa-trophy'
                : 'fa-light fa-layer-group'"
            />
          </div>
          <div class="next-step-info__details">
            <span class="next-step-info__name">
              {{ isGameRedirect
                ? t('templateBuilder.funnelStepSelector.gameResultStep')
                : selectedRedirectLabel }}
            </span>
          </div>
          <Button
            :label="t('templateBuilder.updateStep')"
            icon="fa-light fa-pen"
            class="p-button-outlined p-button-sm next-step-info__btn"
            @click="showNextStepGrid = true"
          />
        </div>
      </div>
    </div>

    <!-- Funnel mode: next step grid (same as BaseLink) -->
    <div
      v-if="linkMode === 'funnel' && showNextStepGrid"
      class="wysiwyg-link-dialog__funnel-section"
    >
      <div class="next-step-grid">
        <div
          v-for="(step, index) in nextStepOptions"
          :key="index"
          class="next-step-card"
          :class="{
            'next-step-card--selected': selectedNextStep === step.value,
            'next-step-card--disabled': step.disabled
          }"
          @click="step.disabled ? null : selectedNextStep = step.value"
        >
          <div class="next-step-card__icon">
            <i :class="step.icon" />
          </div>
          <span class="next-step-card__label">{{ step.label }}</span>
          <span class="next-step-card__desc">{{ step.subtitle }}</span>
          <div
            v-if="step.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP && selectedNextStep === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP"
            class="next-step-card__selector"
            @click.stop
          >
            <FunnelStepSelector
              v-model="selectedFunnelStep"
              :funnel-steps="allFunnelSteps"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- TemplateSelectorV2 for DISPLAY_POPUP -->
    <TemplateSelectorV2
      v-if="displayTemplateModal"
      type-templates="popup"
      origin-call="templateBuilder"
      :id-campaign="idCampaign"
      :duplicate="true"
      :force-duplicate="true"
      :base-link-data="baseLinkData"
      @on-choose-template="handleChooseTemplate"
      @on-close-dialog="displayTemplateModal = false"
    />

    <template #footer>
      <div class="wysiwyg-link-dialog__footer">
        <Button
          v-if="isEditing"
          :label="t('templateBuilder.wysiwygLinkDialog.removeLink')"
          icon="fa-light fa-link-slash"
          text
          class="p-button-danger wysiwyg-link-dialog__remove-btn"
          @click="handleRemoveLink"
        />
        <div class="wysiwyg-link-dialog__footer-actions">
          <Button
            :label="t('templateBuilder.wysiwygLinkDialog.cancel')"
            text
            class="p-button-secondary"
            @click="handleCancel"
          />
          <Button
            :label="t('templateBuilder.wysiwygLinkDialog.confirm')"
            :disabled="!canConfirm"
            :loading="loading"
            outlined
            class="p-button-primary"
            @click="handleConfirm"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import { DropdownOption, NextStepEnum } from '@/types';
import FunnelStepSelector from '@/components/template-builder/FunnelStepSelector.vue';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import {
  TemplateEditorState,
  FunnelStep,
  addStepToFunnel,
  addTemplateToFunnels,
  discoverAndAttachSubFunnels,
  createNextStepTemplate,
  generateNextStepName,
  prepareTemplate,
  syncGameFunnelStepsForTemplate,
  hasGameWidgetInOtherFunnelTemplates,
} from '@/composables/template-editor/TemplateEditor';
import {
  isDisplayTemplate,
  isEmailTemplate,
  getTemplateParentTypeByType,
} from '@/components/template-builder/utils/helpers';
import { UserState } from '@/composables/User';
import { showToastError } from '@/helpers';
import { store } from '@/store';
import { Get } from '@/composables/GraphQL';
import { Templates } from '@/types/generated-types/graphql';

export default defineComponent({
  name: 'WysiwygLinkDialog',

  components: {
    Dialog,
    Button,
    Dropdown,
    InputText,
    FunnelStepSelector,
    TemplateSelectorV2,
    VariablesButton,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    initialUrl: {
      type: String,
      default: '',
    },

    initialText: {
      type: String,
      default: '',
    },

    showFunnelOption: {
      type: Boolean,
      default: false,
    },

    funnelOnly: {
      type: Boolean,
      default: false,
    },

    variablesList: {
      type: Array as PropType<DropdownOption[]>,
      default: () => [],
    },
  },

  emits: ['update:visible', 'confirm', 'remove'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const urlValue = ref('');
    const textValue = ref('');
    const linkMode = ref<'url' | 'funnel'>('url');
    const loading = ref(false);
    const selectedNextStep = ref<NextStepEnum | null>(null);
    const selectedFunnelStep = ref<FunnelStep | null>(null);
    const showNextStepGrid = ref(false);
    const displayTemplateModal = ref(false);
    const urlInputRef = ref();
    const cursorPosition = ref(0);

    // Track cursor position in URL input for variable insertion
    const saveCursorPosition = () => {
      const input = urlInputRef.value?.$el || urlInputRef.value;
      if (input) {
        cursorPosition.value = input.selectionStart ?? urlValue.value.length;
      }
    };

    const insertVariable = (variable: string) => {
      const current = urlValue.value ?? '';
      const pos = cursorPosition.value;
      urlValue.value = current.substring(0, pos) + variable + current.substring(pos);
      cursorPosition.value = pos + variable.length;
    };

    // Track the currently selected redirect (for the info card)
    const selectedRedirectLabel = ref('');
    const isGameRedirect = ref(false);

    const isVisible = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    const isEditing = computed(() => !!props.initialUrl);

    const isInitialRedirect = computed(() => {
      const url = props.initialUrl;
      return url.includes('#redirect_') || url.includes('#spin_redirect_');
    });

    const hasSelectedRedirect = computed(() => isInitialRedirect.value || selectedRedirectLabel.value);

    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? null);

    // baseLinkData — provides context for TemplateSelectorV2 to correctly
    // name and link the duplicated template (same logic as BaseLink.vue)
    const baseLinkData = computed(() => {
      const namePrimaryTemplate = TemplateEditorState?.template?.informations.name;
      const translationsPrimaryTemplate = TemplateEditorState?.template?.translations ?? [];
      const uniqueRedirects = new Set();

      translationsPrimaryTemplate.forEach((translation: any) => {
        const { value } = translation;
        const match = value.match(/#(?:spin_)?redirect_(.+)/);
        if (match) {
          try {
            const idRedirect = window.atob(match[1]);
            if (!idRedirect.match(new RegExp('[a-zA-Z]'))) {
              uniqueRedirects.add(idRedirect);
            }
          } catch (e) { /* invalid base64 */ }
        }
      });

      const countRedirectTemplate = uniqueRedirects.size;

      return {
        namePrimaryTemplate,
        countRedirectTemplate,
        originTemplateId: TemplateEditorState?.template?.id,
      };
    });

    // Initialize values when dialog opens
    watch(() => props.visible, async (val) => {
      if (val) {
        selectedNextStep.value = null;
        selectedFunnelStep.value = null;
        loading.value = false;
        showNextStepGrid.value = false;
        displayTemplateModal.value = false;
        selectedRedirectLabel.value = '';
        textValue.value = props.initialText || '';
        isGameRedirect.value = false;

        if (props.funnelOnly || isInitialRedirect.value) {
          linkMode.value = 'funnel';
          urlValue.value = '';

          if (isInitialRedirect.value) {
            // Resolve redirect label from initial URL
            const url = props.initialUrl;
            if (url.includes('game_result') || url.includes('game_prizes')) {
              isGameRedirect.value = true;
            } else {
              // Extract template ID from #redirect_{base64} or #spin_redirect_{base64}
              const parts = url.includes('#spin_redirect_')
                ? url.split('#spin_redirect_')
                : url.split('#redirect_');
              const encoded = parts[1] || '';
              if (encoded) {
                try {
                  const templateId = atob(encoded);
                  const { item } = await Get<Templates>({
                    name: 'Templates',
                    id: Number(templateId),
                    keyName: 'id_template',
                    fields: ['id_template', 'label'],
                  });
                  if (item) {
                    selectedRedirectLabel.value = item.label || `Template #${templateId}`;
                  }
                } catch {
                  selectedRedirectLabel.value = `Template`;
                }
              }
            }
          } else {
            // funnelOnly with no existing redirect — show grid immediately
            showNextStepGrid.value = true;
          }
        } else {
          linkMode.value = 'url';
          urlValue.value = props.initialUrl || '';

          nextTick(() => {
            if (urlInputRef.value) {
              urlInputRef.value.$el?.focus?.();
            }
          });
        }
      }
    });

    // Link mode options
    const linkModeOptions = computed(() => [
      {
        label: t('templateBuilder.wysiwygLinkDialog.modeUrl'),
        value: 'url',
      },
      {
        label: t('templateBuilder.wysiwygLinkDialog.modeFunnel'),
        value: 'funnel',
      },
    ]);

    const handleLinkModeChange = () => {
      if (linkMode.value === 'funnel') {
        // If no existing redirect, show grid immediately
        if (!hasSelectedRedirect.value) {
          showNextStepGrid.value = true;
        }
      }
    };

    // Funnel steps
    const funnelSteps = computed(() => (
      TemplateEditorState.template ? TemplateEditorState.template.funnel.nextSteps : []
    ));

    // Pass raw funnel steps to FunnelStepSelector — it handles its own filtering
    // and adds the "Game result" option when game steps exist
    const allFunnelSteps = computed(() => {
      const steps = funnelSteps.value;
      // Convert readonly to mutable for FunnelStepSelector compatibility
      return (steps as any[]).map((step: any) => ({
        template: step.template,
        nextSteps: step.nextSteps || [],
      })) as FunnelStep[];
    });

    // Next step options — same logic as BaseLink.vue
    const nextStepIconMap: Record<string, string> = {
      [NextStepEnum.COLLECT_PERSONAL_INFO]: 'fa-light fa-address-card',
      [NextStepEnum.COLLECT_PHONE_NUMBER]: 'fa-light fa-phone',
      [NextStepEnum.OFFER_VOUCHER]: 'fa-light fa-gift',
      [NextStepEnum.EMPTY_STEP]: 'fa-light fa-file',
      [NextStepEnum.CHOOSE_EXISTING_NEXT_STEP]: 'fa-light fa-link',
      [NextStepEnum.DISPLAY_POPUP]: 'fa-light fa-palette',
    };

    const nextStepOptions = computed(() => {
      let stepOptions: NextStepEnum[];
      if (isDisplayTemplate(TemplateEditorState.template?.type)) {
        stepOptions = [
          NextStepEnum.COLLECT_PERSONAL_INFO,
          NextStepEnum.COLLECT_PHONE_NUMBER,
          NextStepEnum.OFFER_VOUCHER,
          NextStepEnum.EMPTY_STEP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
          NextStepEnum.DISPLAY_POPUP,
        ];
      } else {
        // Email templates: only gallery and existing step
        stepOptions = [
          NextStepEnum.DISPLAY_POPUP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
        ];
      }

      return stepOptions.map((option) => {
        const isDisplayPopupRenamedAsNew = isDisplayTemplate(TemplateEditorState.template?.type) && option === NextStepEnum.DISPLAY_POPUP;
        const labelKey = isDisplayPopupRenamedAsNew ? 'chooseNewTemplate' : option;
        return {
          label: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.title`),
          subtitle: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.subtitle`),
          icon: nextStepIconMap[option] || 'fa-light fa-circle',
          value: option,
          disabled: option === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP && !allFunnelSteps.value.length && !hasGameWidgetInOtherFunnelTemplates(),
        };
      });
    });

    // ---- URL Validation ----

    const normalizeUrl = (url: string): string => {
      let normalized = url.trim();

      if (normalized.startsWith('{var=') || normalized.startsWith('{var =')) {
        return normalized;
      }

      const doubleProtocolMatch = normalized.match(/^(https?:\/\/)(https?:\/\/)/i);
      if (doubleProtocolMatch) {
        normalized = normalized.substring(doubleProtocolMatch[1].length);
      }

      if (normalized && !normalized.match(/^(https?:\/\/|mailto:|tel:|#|\{var)/i)) {
        normalized = `https://${normalized}`;
      }

      return normalized;
    };

    const urlError = computed(() => {
      const url = urlValue.value.trim();
      if (!url) return '';
      if (url === 'http://' || url === 'https://') {
        return t('templateBuilder.wysiwygLinkDialog.errors.emptyUrl');
      }
      return '';
    });

    const urlWarning = computed(() => {
      const url = urlValue.value.trim();
      if (!url) return '';

      const hasProtocol = /^https?:\/\//i.test(url);
      if (hasProtocol) {
        const afterProtocol = url.replace(/^https?:\/\//i, '');
        if (afterProtocol.startsWith('{var=')) {
          return t('templateBuilder.wysiwygLinkDialog.warnings.doubleProtocol');
        }
      }

      if (!url.match(/^(https?:\/\/|mailto:|tel:|#|\{var)/i)) {
        return t('templateBuilder.wysiwygLinkDialog.warnings.noProtocol');
      }

      return '';
    });

    // ---- Emit helper ----

    const emitFunnelResult = (href: string) => {
      emit('confirm', { type: 'funnel', url: href, text: textValue.value });
      showNextStepGrid.value = false;
      isVisible.value = false;
    };

    // ---- Handle CHOOSE_EXISTING_NEXT_STEP ----

    const handleExistingStep = async () => {
      if (!selectedFunnelStep.value) {
        showToastError(t('templateBuilder.funnelStepSelector.error'));
        return;
      }

      // Game result
      if (selectedFunnelStep.value.template?.isGameResult) {
        emitFunnelResult('#redirect_game_result');
        return;
      }

      const templateId = selectedFunnelStep.value.template?.id;
      if (templateId) {
        addStepToFunnel(selectedFunnelStep.value);
        emitFunnelResult(`#redirect_${btoa(String(templateId))}`);
      }
    };

    // ---- Handle DISPLAY_POPUP (opens TemplateSelectorV2) ----

    const handleChooseTemplate = async (idTemplate: number) => {
      loading.value = true;
      store.commit('general/showTheSpinner');

      const { item } = await Get<Templates>({
        name: 'Templates',
        id: idTemplate,
        keyName: 'id_template',
        fields: ['id_template', 'label', 'type'],
      });

      if (item) {
        const preparedTemplate = await prepareTemplate(idTemplate, item.label, item.type as string);
        if (preparedTemplate) {
          addTemplateToFunnels(preparedTemplate.template);
          await discoverAndAttachSubFunnels(preparedTemplate.template);
          syncGameFunnelStepsForTemplate(preparedTemplate.template.id);
        }

        displayTemplateModal.value = false;
        store.commit('general/hideTheSpinner');
        loading.value = false;
        emitFunnelResult(`#redirect_${btoa(String(idTemplate))}`);
      } else {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        store.commit('general/hideTheSpinner');
        loading.value = false;
      }
    };

    // ---- Handle template creation steps (COLLECT_PERSONAL_INFO, EMPTY_STEP, etc.) ----

    const handleCreateStep = async () => {
      if (!selectedNextStep.value) return;

      loading.value = true;
      store.commit('general/showTheSpinner');

      const idShop = UserState.activeShop ? UserState.activeShop.id : 0;
      const result = await createNextStepTemplate(
        idShop,
        selectedNextStep.value,
        generateNextStepName(
          TemplateEditorState.template?.informations.name || '',
          getTemplateParentTypeByType(TemplateEditorState.template?.type),
          t,
        ),
        t,
        idCampaign.value,
        'templateBuilder',
      );

      if (!result) {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        store.commit('general/hideTheSpinner');
        loading.value = false;
        return;
      }

      const { template } = result;
      if (template) {
        addTemplateToFunnels(template);
        await discoverAndAttachSubFunnels(template);
      }

      store.commit('general/hideTheSpinner');
      loading.value = false;
      emitFunnelResult(`#redirect_${btoa(String(template.id))}`);
    };

    // ---- Main Confirm ----

    const canConfirm = computed(() => {
      if (linkMode.value === 'url') {
        return urlValue.value.trim().length > 0 && !urlError.value;
      }
      if (linkMode.value === 'funnel') {
        // If showing info card (existing redirect), confirm keeps it
        if (hasSelectedRedirect.value && !showNextStepGrid.value) {
          return true;
        }
        // Grid is shown: need a selection
        if (!selectedNextStep.value) return false;
        if (selectedNextStep.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP) {
          return !!selectedFunnelStep.value;
        }
        return true;
      }
      return false;
    });

    const handleConfirm = async () => {
      if (!canConfirm.value) return;

      if (linkMode.value === 'url') {
        const finalUrl = normalizeUrl(urlValue.value);
        emit('confirm', { type: 'url', url: finalUrl, text: textValue.value });
        isVisible.value = false;
        return;
      }

      if (linkMode.value === 'funnel') {
        // If existing redirect and grid not shown, keep current value
        if (hasSelectedRedirect.value && !showNextStepGrid.value) {
          // No change, just close
          isVisible.value = false;
          return;
        }

        if (selectedNextStep.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP) {
          await handleExistingStep();
        } else if (selectedNextStep.value === NextStepEnum.DISPLAY_POPUP) {
          displayTemplateModal.value = true;
        } else {
          // COLLECT_PERSONAL_INFO, COLLECT_PHONE_NUMBER, OFFER_VOUCHER, EMPTY_STEP
          await handleCreateStep();
        }
      }
    };

    const handleCancel = () => {
      displayTemplateModal.value = false;
      isVisible.value = false;
    };

    const handleRemoveLink = () => {
      emit('remove');
      displayTemplateModal.value = false;
      isVisible.value = false;
    };

    return {
      t,
      isVisible,
      isEditing,
      linkMode,
      linkModeOptions,
      urlValue,
      textValue,
      urlInputRef,
      saveCursorPosition,
      insertVariable,
      urlError,
      urlWarning,
      loading,
      selectedNextStep,
      selectedFunnelStep,
      allFunnelSteps,
      nextStepOptions,
      showNextStepGrid,
      hasSelectedRedirect,
      selectedRedirectLabel,
      isGameRedirect,
      displayTemplateModal,
      idCampaign,
      baseLinkData,
      canConfirm,
      handleConfirm,
      handleCancel,
      handleRemoveLink,
      handleChooseTemplate,
      handleLinkModeChange,
      NextStepEnum,
    };
  },
});
</script>

<style scoped lang="scss">
.wysiwyg-link-dialog {
  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1.1rem;
    font-weight: 600;

    i {
      font-size: 1.2rem;
      color: var(--primary-color);
    }
  }

  &__text-section {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 0.875rem;
    }
  }

  &__type-selector {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 0.875rem;
    }
  }

  &__url-section {
    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 0.875rem;
    }
  }

  &__url-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__variables-btn {
    flex-shrink: 0;
    width: 140px;
  }

  &__warning {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--orange-500);
    margin-top: 4px;

    i {
      font-size: 0.75rem;
    }
  }

  &__funnel-info {
    margin-top: 4px;
  }

  &__funnel-section {
    margin-top: 4px;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__footer-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }

  &__remove-btn {
    margin-right: auto;
  }
}

// Reuse next-step styles from BaseLink
.next-step-info {
  &__card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--surface-ground);
    border-radius: 8px;
    border: 1px solid var(--surface-border);
  }

  &__icon {
    font-size: 1.5rem;
    color: var(--primary-color);
  }

  &__details {
    flex: 1;
  }

  &__name {
    font-weight: 600;
    font-size: 0.9rem;
  }

  &__btn {
    white-space: nowrap;
  }
}

.next-step-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.next-step-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border: 2px solid var(--surface-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover:not(.next-step-card--disabled) {
    border-color: var(--primary-color);
    background: var(--primary-50);
  }

  &--selected {
    border-color: var(--primary-color);
    background: var(--primary-50);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__icon {
    font-size: 1.5rem;
    margin-bottom: 8px;
    color: var(--primary-color);
  }

  &__label {
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 4px;
  }

  &__desc {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
  }

  &__selector {
    margin-top: 8px;
    width: 100%;
  }
}
</style>
